import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import "./ImportVentas.css";
import { toast } from "react-toastify";

// ahora solo XLSX
const ACCEPT_EXT = [".xlsx"];
const ACCEPT_MIME = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

function isValidFile(file) {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  const byExt = ACCEPT_EXT.some((ext) => name.endsWith(ext));
  const byMime = ACCEPT_MIME.includes(file.type);
  return byExt || byMime;
}

export default function ImportVentasModal({
  show,
  banderaSubir,
  toggle,
  onImport,
  onImportProductos,
  onImportSantander,
  loading = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const acceptAttr = useMemo(() => ACCEPT_EXT.join(","), []);

  const resetState = () => {
    setDragOver(false);
    setFile(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (show) resetState();
  }, [show]);

  const close = () => {
    if (loading) return;
    resetState();
    toggle?.();
  };

  const pickFile = () => {
    if (loading) return;
    inputRef.current?.click();
  };

  const runImport = async (f) => {
    if (!f) return;

    if (!isValidFile(f)) {
      setFile(null);
      setError("Archivo no válido. Solo se aceptan archivos .XLSX.");
      toast.info("Archivo no válido. Solo se aceptan archivos .XLSX.");
      return;
    }

    setError("");
    setFile(f);

    try {
      if (banderaSubir === "productos") {
        await onImportProductos?.(f);
      } else if (banderaSubir === "santander") {
        await onImportSantander?.(f);
      } else {
        await onImport?.(f);
      }
    } catch (e) {
      toast.error("No se pudo importar el archivo.");
    }
  };

  const onChangeInput = async (e) => {
    const f = e.target.files?.[0];
    await runImport(f);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    if (loading) return;
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    await runImport(f);
  };

  const getTitle = () => {
    if (banderaSubir === "productos") return "Productos";
    if (banderaSubir === "santander") return "Movimientos Santander";
    return "Ventas";
  };

  return (
    <Modal
      isOpen={!!show}
      toggle={close}
      centered
      size="lg"
      backdrop="static"
      keyboard={!loading}
      className="ivm-rs-modal"
    >
      <ModalHeader toggle={loading ? undefined : close}>
        Importar {getTitle()}
      </ModalHeader>

      <ModalBody>
        <div
          className={`ivm-dropzone ${dragOver ? "is-over" : ""} ${loading ? "is-loading" : ""}`}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!loading) setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!loading) setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={onDrop}
        >
          <div className="ivm-icon" aria-hidden="true">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path d="M14 2v5h5" stroke="currentColor" strokeWidth="1.6" />
              <path d="M8 13h8M8 16h8" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </div>

          <div className="ivm-mainText">
            {loading ? "Importando archivo..." : "Arrastra y suelta el archivo aquí"}
          </div>

          <button
            className="ivm-pickBtn"
            onClick={pickFile}
            type="button"
            disabled={loading}
          >
            {loading ? "Procesando..." : "O haz clic para seleccionar un archivo"}
          </button>

          <div className="ivm-help">Solo se aceptan archivos .XLSX.</div>

          <input
            ref={inputRef}
            type="file"
            accept={acceptAttr}
            onChange={onChangeInput}
            className="ivm-hiddenInput"
            disabled={loading}
          />

          {file && !error && (
            <div className="ivm-filePill">
              <span className="ivm-fileName">{file.name}</span>
            </div>
          )}

          {error ? <div className="ivm-error">{error}</div> : null}

          {loading && (
            <div className="ivm-loadingBox">
              <div className="ivm-spinner" />
              <div className="ivm-loadingText">Subiendo e importando archivo...</div>
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter className="ivm-footer-rs">
        <Button
          color="danger"
          outline
          onClick={close}
          disabled={loading}
          className="ivm-btn"
        >
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}